import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Table,
  InputNumber,
  Space,
  Alert,
  message,
  Row,
  Col,
  Divider,
  Card,
  Statistic,
  AutoComplete
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, UploadOutlined, FileTextOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import {
  JournalEntry,
  JournalEntryLine,
  CreateJournalEntryInput,
  Journal,
  journalService
} from '@/services/journalService';
import { Account } from '@/services/accountService';
import hrService, { Department } from '@/services/hrService';
import customerService, { Customer } from '@/services/customerService';
import invoiceService, { Invoice } from '@/services/invoiceService';
import vendorBillService, { VendorBill } from '@/services/vendorBillService';
import apiService from '@/services/apiService';

interface VendorLite {
  id: string;
  name: string;
  code?: string;
}

const { TextArea } = Input;

interface JournalEntryFormProps {
  entry?: JournalEntry;
  journals: Journal[];
  accounts: Account[];
  onSubmit: (values: CreateJournalEntryInput) => Promise<void>;
  onSubmitAndPost?: (values: CreateJournalEntryInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

interface EntryLineWithKey extends Omit<JournalEntryLine, 'id'> {
  key: string;
}

const JournalEntryForm: React.FC<JournalEntryFormProps> = ({
  entry,
  journals,
  accounts,
  onSubmit,
  onSubmitAndPost,
  onCancel,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [lines, setLines] = useState<EntryLineWithKey[]>([]);
  const [validationError, setValidationError] = useState<string>('');
  const [totalDebit, setTotalDebit] = useState<number>(0);
  const [totalCredit, setTotalCredit] = useState<number>(0);
  const [departments, setDepartments] = useState<Department[]>([]);

  // Departments are still fetched, but ONLY to render a line saved before this
  // field was unlinked from HR. They are never offered as choices.
  const legacyDepartmentName = (departmentId?: string): string => {
    if (!departmentId) return '';
    const d = departments.find(x => x.id === departmentId);
    return d ? `${d.code} - ${d.name}` : '';
  };

  // Lookup lists for the Partner / Invoice No combo boxes. They are populated
  // best-effort — failure to fetch any one list still lets the user type free
  // text into the AutoComplete.
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vendors, setVendors] = useState<VendorLite[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [vendorBills, setVendorBills] = useState<VendorBill[]>([]);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const departments = await hrService.department.getAll();
        setDepartments(departments);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch lookup lists for Partner / Invoice No combos. All four fire in
  // parallel; a failure on any one is logged but not surfaced — the combo
  // still works as plain free text.
  useEffect(() => {
    const fetchLookups = async () => {
      const [custRes, venRes, invRes, billRes] = await Promise.allSettled([
        customerService.getAllCustomers({ isActive: true }),
        apiService.get<{ data: VendorLite[] }>('/accounting/vendors'),
        invoiceService.getAllInvoices(),
        vendorBillService.getAllBills()
      ]);
      if (custRes.status === 'fulfilled') setCustomers(custRes.value);
      else console.error('Failed to fetch customers:', custRes.reason);
      if (venRes.status === 'fulfilled') setVendors(venRes.value.data || []);
      else console.error('Failed to fetch vendors:', venRes.reason);
      if (invRes.status === 'fulfilled') setInvoices(invRes.value);
      else console.error('Failed to fetch invoices:', invRes.reason);
      if (billRes.status === 'fulfilled') setVendorBills(billRes.value);
      else console.error('Failed to fetch vendor bills:', billRes.reason);
    };
    fetchLookups();
  }, []);

  // Combo box options. Stored values are the human-readable name / number so
  // the JE list, exports and PDFs render correctly without any backend lookup.
  // Free-text fallback works trivially because AutoComplete accepts any value.
  const partnerOptions = useMemo(() => {
    const customerOpts = customers.map(c => ({
      value: c.name,
      label: `${c.name} · Customer`
    }));
    const vendorOpts = vendors.map(v => ({
      value: v.name,
      label: `${v.name} · Vendor${v.code ? ` (${v.code})` : ''}`
    }));
    return [...customerOpts, ...vendorOpts];
  }, [customers, vendors]);

  const invoiceNoOptions = useMemo(() => {
    const invoiceOpts = invoices.map(i => ({
      value: i.invoiceNo,
      label: `${i.invoiceNo} · Invoice${i.customer?.name ? ` (${i.customer.name})` : ''}`
    }));
    const billOpts = vendorBills.map(b => ({
      value: b.billNo,
      label: `${b.billNo} · Bill${b.vendor?.name ? ` (${b.vendor.name})` : ''}`
    }));
    return [...invoiceOpts, ...billOpts];
  }, [invoices, vendorBills]);

  // Inline create-department from the row's Department dropdown. Generates a
  // deterministic code from the typed name (UPPER_SNAKE + short random suffix)
  // so the user doesn't have to deal with the code/name split here. If they
  // need anything fancier they can edit it later from HR > Departments.

  /**
   * Put the next number for the chosen journal into the Entry # field.
   *
   * The number carries the journal's code, the year and the month, so it only
   * makes sense alongside the journal it was drawn from. Before this, the field
   * kept whatever was there — an entry could read GJ-0003 while sitting in the
   * BNK2 journal, which is what was reported.
   *
   * Only ever called from a deliberate change of journal or date, never while
   * loading an existing entry, so opening a record never renumbers it.
   */
  const applyNextEntryNumber = async (journalId?: string, date?: any) => {
    const jId = journalId ?? form.getFieldValue('journalId');
    if (!jId) return;
    const d = date ?? form.getFieldValue('date');
    try {
      const next = await journalService.getNextEntryNumber(
        jId,
        d ? dayjs(d).format('YYYY-MM-DD') : undefined
      );
      if (next) form.setFieldsValue({ name: next });
    } catch {
      // Leave the field as it is — the server generates the number on save
      // anyway, so a failed preview costs nothing.
    }
  };

  // Initialize form with entry data if editing
  useEffect(() => {
    if (entry) {
      form.setFieldsValue({
        name: entry.name,
        journalId: entry.journalId,
        date: dayjs(entry.date),
        accountingDate: entry.accountingDate ? dayjs(entry.accountingDate) : null,
        ref: entry.ref,
        narration: entry.narration,
        chequeNo: entry.chequeNo,
        partnerId: entry.partnerId,
        invoiceNo: entry.invoiceNo
      });

      const initialLines: EntryLineWithKey[] = entry.lines.map((line, index) => ({
        key: `line-${index}`,
        accountId: line.accountId,
        name: line.name,
        debit: Number(line.debit),
        credit: Number(line.credit),
        partnerId: line.partnerId,
        currencyId: line.currencyId,
        amountCurrency: line.amountCurrency ? Number(line.amountCurrency) : undefined,
        analyticAccountId: line.analyticAccountId,
        departmentId: line.departmentId,
        departmentName: (line as any).departmentName
      }));

      setLines(initialLines);
    } else {
      // Initialize with 2 empty lines for new entry
      addLine();
      addLine();
    }
  }, [entry, form]);

  // Calculate totals whenever lines change
  useEffect(() => {
    const debitTotal = lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
    const creditTotal = lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);
    setTotalDebit(debitTotal);
    setTotalCredit(creditTotal);

    // Only validate if at least one line has an account selected
    const touchedLines = lines.filter(line => line.accountId);
    if (touchedLines.length > 0) {
      // Validate only touched lines
      const validation = journalService.validateDoubleEntry(touchedLines as JournalEntryLine[]);
      if (!validation.valid) {
        setValidationError(validation.message || '');
      } else {
        setValidationError('');
      }
    } else {
      // Clear error if no lines have been touched yet
      setValidationError('');
    }
  }, [lines]);

  const addLine = () => {
    const newLine: EntryLineWithKey = {
      key: `line-${Date.now()}`,
      accountId: '',
      name: '',
      debit: 0,
      credit: 0
    };
    setLines([...lines, newLine]);
  };

  const removeLine = (key: string) => {
    if (lines.length <= 2) {
      message.warning('Journal entry must have at least 2 lines');
      return;
    }
    setLines(lines.filter(line => line.key !== key));
  };

  const updateLine = (key: string, field: string, value: any) => {
    setLines(lines.map(line => {
      if (line.key === key) {
        // If updating debit, clear credit and vice versa
        if (field === 'debit' && value > 0) {
          return { ...line, debit: value, credit: 0 };
        } else if (field === 'credit' && value > 0) {
          return { ...line, credit: value, debit: 0 };
        }
        return { ...line, [field]: value };
      }
      return line;
    }));
  };

  const validateAndPrepareData = async (): Promise<CreateJournalEntryInput | null> => {
    try {
      // Validate form fields
      const values = await form.validateFields();

      // Validate lines
      if (lines.length < 2) {
        message.error('Journal entry must have at least 2 lines');
        return null;
      }

      // Validate all lines have account selected
      const invalidLines = lines.filter(line => !line.accountId);
      if (invalidLines.length > 0) {
        message.error('All lines must have an account selected');
        return null;
      }

      // Validate double-entry
      const validation = journalService.validateDoubleEntry(lines as JournalEntryLine[]);
      if (!validation.valid) {
        message.error(validation.message);
        return null;
      }

      // Prepare submission data
      const submitData: CreateJournalEntryInput = {
        // Empty/whitespace → undefined so the backend auto-generates.
        name: values.name?.trim() || undefined,
        journalId: values.journalId,
        // Calendar dates, not instants. toISOString() shifted a Hong Kong date
        // back eight hours, so 1 September left the browser as 31 August and the
        // entry was numbered into the wrong month (client feedback #22). An
        // accounting date has no time of day, so sending one was always wrong.
        date: values.date.format('YYYY-MM-DD'),
        accountingDate: values.accountingDate ? values.accountingDate.format('YYYY-MM-DD') : undefined,
        ref: values.ref || undefined,
        narration: values.narration || undefined,
        chequeNo: values.chequeNo || undefined,
        partnerId: values.partnerId || undefined,
        invoiceNo: values.invoiceNo || undefined,
        lines: lines.map(line => ({
          accountId: line.accountId,
          name: line.name || undefined,
          debit: Number(line.debit),
          credit: Number(line.credit),
          partnerId: line.partnerId || undefined,
          currencyId: line.currencyId || undefined,
          amountCurrency: line.amountCurrency || undefined,
          analyticAccountId: line.analyticAccountId || undefined,
          departmentId: line.departmentId || undefined,
          departmentName: (line as any).departmentName || undefined
        }))
      };

      return submitData;
    } catch (error) {
      console.error('Form validation failed:', error);
      return null;
    }
  };

  const handleSubmit = async () => {
    const submitData = await validateAndPrepareData();
    if (submitData) {
      await onSubmit(submitData);
    }
  };

  const handleSubmitAndPost = async () => {
    const submitData = await validateAndPrepareData();
    if (submitData && onSubmitAndPost) {
      await onSubmitAndPost(submitData);
    }
  };

  const getDifference = () => {
    return Math.abs(totalDebit - totalCredit);
  };

  const isBalanced = () => {
    return getDifference() < 0.01;
  };

  const columns = [
    {
      title: 'Account',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 250,
      render: (accountId: string, record: EntryLineWithKey) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Select account"
          value={accountId || undefined}
          onChange={(value) => updateLine(record.key, 'accountId', value)}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={accounts
            .filter(acc => !acc.deprecated)
            .map(acc => ({
              value: acc.id,
              label: `${acc.code} - ${acc.name}`
            }))}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
      width: 240,
      render: (name: string, record: EntryLineWithKey) => (
        // Multi-line: press Enter for line breaks. autoSize grows the row
        // as the user types so all wrapped lines stay visible.
        <TextArea
          value={name || ''}
          onChange={(e) => updateLine(record.key, 'name', e.target.value)}
          placeholder="Line description (Enter for new line)"
          autoSize={{ minRows: 1, maxRows: 6 }}
          style={{ resize: 'none' }}
        />
      )
    },
    {
      title: 'Partner',
      dataIndex: 'partnerId',
      key: 'partnerId',
      width: 200,
      // Per-line counterparty. The column already existed in the database and
      // is already resolved for the ledger and the export, but the form gave no
      // way to set it — so a user could only tag the whole entry, never the
      // individual lines. Same control as the header Partner above.
      render: (partnerId: string, record: EntryLineWithKey) => (
        <AutoComplete
          style={{ width: '100%' }}
          size="small"
          options={partnerOptions}
          value={partnerId || undefined}
          placeholder="Customer / Vendor"
          allowClear
          onChange={(value) => updateLine(record.key, 'partnerId', value)}
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      )
    },
    {
      title: 'Department',
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 200,
      // Free text, not a picker — unlinked from the HR department list at the
      // client's request. Lines saved before the change show their old name.
      render: (_: any, record: EntryLineWithKey) => (
        <Input
          size="small"
          placeholder="Department"
          value={(record as any).departmentName ?? legacyDepartmentName(record.departmentId)}
          onChange={(e) => updateLine(record.key, 'departmentName' as any, e.target.value)}
        />
      )
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      width: 150,
      align: 'right' as const,
      render: (debit: number, record: EntryLineWithKey) => (
        <InputNumber
          style={{ width: '100%' }}
          value={debit}
          onChange={(value) => updateLine(record.key, 'debit', value || 0)}
          min={0}
          precision={2}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
        />
      )
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      width: 150,
      align: 'right' as const,
      render: (credit: number, record: EntryLineWithKey) => (
        <InputNumber
          style={{ width: '100%' }}
          value={credit}
          onChange={(value) => updateLine(record.key, 'credit', value || 0)}
          min={0}
          precision={2}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
        />
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: EntryLineWithKey) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeLine(record.key)}
          disabled={lines.length <= 2}
        />
      )
    }
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          date: dayjs()
        }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="name"
              label="Entry #"
              tooltip="Filled in from the journal you select, as {Journal code}/{Year}/{Month}/{Number} — for example BNK2/2026/08/0003. Choosing a different journal or moving the date to another month replaces it. You can still type your own number; it must be unique within the company. The Reference field below is for external numbers such as an invoice number."
              rules={[
                { max: 64, message: 'Entry # must be 64 characters or fewer' }
              ]}
            >
              <Input
                placeholder="(Auto-generated on save)"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="journalId"
              label="Journal"
              rules={[{ required: true, message: 'Please select a journal' }]}
            >
              <Select
                showSearch
                placeholder="Select journal"
                onChange={(journalId: string) => applyNextEntryNumber(journalId)}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={journals
                  .filter(j => j.active)
                  .map(j => ({
                    value: j.id,
                    label: `${j.code} - ${j.name}`
                  }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="date"
              label="Entry Date"
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                // The number carries the year and month too, so moving the date
                // into another month has to redraw it.
                onChange={(d) => applyNextEntryNumber(undefined, d)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="accountingDate" label="Accounting Date">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Optional - defaults to Entry Date" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="ref" label="Reference">
              <Input placeholder="External reference (e.g., invoice number)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="partnerId"
              label="Partner"
              tooltip="Pick a Customer or Vendor, or type any name. Stored as a plain string."
            >
              <AutoComplete
                options={partnerOptions}
                placeholder="Search Customer / Vendor, or type a name"
                allowClear
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="chequeNo" label="Cheque No">
              <Input placeholder="Cheque number (if applicable)" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="invoiceNo"
              label="Invoice No"
              tooltip="Pick an existing Invoice or Vendor Bill number, or type any reference."
            >
              <AutoComplete
                options={invoiceNoOptions}
                placeholder="Search Invoice / Bill, or type a number"
                allowClear
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="narration" label="Description">
          <TextArea
            rows={3}
            placeholder="Enter description of this journal entry"
          />
        </Form.Item>
      </Form>

      <Divider orientation="left">Entry Lines</Divider>

      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <input
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            id="journal-csv-upload"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                // Parse CSV: Account,Partner,Name,Analytic Account,Amount Currency,Currency,Debit,Credit
                Papa.parse(file, {
                  header: true,
                  skipEmptyLines: true,
                  complete: (results) => {
                    try {
                      const importedLines: EntryLineWithKey[] = [];
                      let successCount = 0;
                      let errorCount = 0;

                      const notFoundAccounts: string[] = [];

                      for (const row of results.data as any[]) {
                        // Find account by code
                        const accountCode = row['Account']?.trim();
                        if (!accountCode) continue;

                        const account = accounts.find(
                          acc => acc.code === accountCode || acc.name === accountCode
                        );

                        if (!account) {
                          console.warn(`Account not found: ${accountCode}`);
                          if (!notFoundAccounts.includes(accountCode)) {
                            notFoundAccounts.push(accountCode);
                          }
                          errorCount++;
                          continue;
                        }

                        const debit = parseFloat(row['Debit'] || '0') || 0;
                        const credit = parseFloat(row['Credit'] || '0') || 0;

                        importedLines.push({
                          key: `imported-${Date.now()}-${successCount}`,
                          accountId: account.id,
                          name: row['Name']?.trim() || '',
                          debit,
                          credit,
                          partnerId: undefined, // TODO: Add partner lookup if needed
                          currencyId: undefined, // TODO: Add currency lookup if needed
                          amountCurrency: parseFloat(row['Amount Currency'] || '0') || undefined,
                          analyticAccountId: undefined // TODO: Add analytic account lookup if needed
                        });
                        successCount++;
                      }

                      if (importedLines.length > 0) {
                        setLines([...lines, ...importedLines]);
                        message.success(`Imported ${successCount} journal items${errorCount > 0 ? ` (${errorCount} skipped)` : ''}`);
                        if (notFoundAccounts.length > 0) {
                          message.warning(`Accounts not found: ${notFoundAccounts.join(', ')}. Please create these accounts first.`);
                        }
                      } else {
                        if (notFoundAccounts.length > 0) {
                          message.error(`No valid journal items found. Accounts not found: ${notFoundAccounts.join(', ')}. Please create these accounts first.`);
                        } else {
                          message.error('No valid journal items found in CSV');
                        }
                      }
                    } catch (error) {
                      console.error('CSV parsing error:', error);
                      message.error('Failed to parse CSV file');
                    }
                  },
                  error: (error) => {
                    console.error('CSV parsing error:', error);
                    message.error('Failed to read CSV file');
                  }
                });
              }
              // Reset file input
              e.target.value = '';
            }}
          />
          <Button
            icon={<UploadOutlined />}
            onClick={() => document.getElementById('journal-csv-upload')?.click()}
          >
            Import Journal Items (CSV)
          </Button>
        </Col>
        <Col>
          <input
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            id="invoice-csv-upload"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                // Parse CSV: Product,Description,Ordered Qty,Unit of Measure,Unit Price,Taxes
                Papa.parse(file, {
                  header: true,
                  skipEmptyLines: true,
                  complete: (results) => {
                    try {
                      const importedLines: EntryLineWithKey[] = [];
                      let successCount = 0;
                      let errorCount = 0;
                      let totalAmount = 0;

                      // Find revenue account (you may need to adjust the account code/name)
                      const revenueAccount = accounts.find(
                        acc => acc.accountType === 'REVENUE' || acc.code?.startsWith('4') || acc.name.toLowerCase().includes('revenue') || acc.name.toLowerCase().includes('sales')
                      );

                      if (!revenueAccount) {
                        message.error('No revenue account found. Please create a revenue account first.');
                        return;
                      }

                      for (const row of results.data as any[]) {
                        const product = row['Product']?.trim();
                        const description = row['Description']?.trim();
                        const qty = parseFloat(row['Ordered Qty'] || '0') || 0;
                        const unitPrice = parseFloat(row['Unit Price'] || '0') || 0;

                        // Skip empty rows
                        if (!product && !description && qty === 0 && unitPrice === 0) {
                          continue;
                        }

                        const lineTotal = qty * unitPrice;
                        totalAmount += lineTotal;

                        if (lineTotal > 0) {
                          // Create revenue entry (credit side)
                          const lineName = product
                            ? `${product}${description ? ' - ' + description : ''}`
                            : description || 'Invoice item';

                          importedLines.push({
                            key: `invoice-${Date.now()}-${successCount}`,
                            accountId: revenueAccount.id,
                            name: lineName,
                            debit: 0,
                            credit: lineTotal,
                            partnerId: undefined,
                            currencyId: undefined,
                            amountCurrency: undefined,
                            analyticAccountId: undefined
                          });
                          successCount++;
                        } else {
                          errorCount++;
                        }
                      }

                      if (importedLines.length > 0) {
                        // Add receivable account entry (debit side) to balance the invoice
                        // Receivable accounts are ASSET type with "receivable" or "debtor" in the name
                        const receivableAccount = accounts.find(
                          acc => (acc.accountType === 'ASSET' &&
                                 (acc.name.toLowerCase().includes('receivable') ||
                                  acc.name.toLowerCase().includes('debtor') ||
                                  acc.code?.startsWith('11') ||
                                  acc.code?.startsWith('12')))
                        );

                        if (receivableAccount && totalAmount > 0) {
                          importedLines.unshift({
                            key: `invoice-receivable-${Date.now()}`,
                            accountId: receivableAccount.id,
                            name: 'Account Receivable',
                            debit: totalAmount,
                            credit: 0,
                            partnerId: undefined,
                            currencyId: undefined,
                            amountCurrency: undefined,
                            analyticAccountId: undefined
                          });
                        }

                        setLines([...lines, ...importedLines]);
                        message.success(`Imported ${successCount} invoice items (Total: ${totalAmount.toFixed(2)})${errorCount > 0 ? ` (${errorCount} skipped)` : ''}`);
                      } else {
                        message.error('No valid invoice items found in CSV');
                      }
                    } catch (error) {
                      console.error('CSV parsing error:', error);
                      message.error('Failed to parse invoice CSV file');
                    }
                  },
                  error: (error) => {
                    console.error('CSV parsing error:', error);
                    message.error('Failed to read CSV file');
                  }
                });
              }
              // Reset file input
              e.target.value = '';
            }}
          />
          <Button
            icon={<FileTextOutlined />}
            onClick={() => document.getElementById('invoice-csv-upload')?.click()}
          >
            Import Invoice/Credit Notes (CSV)
          </Button>
        </Col>
      </Row>

      <Table
        dataSource={lines}
        columns={columns}
        pagination={false}
        size="small"
        rowKey="key"
        footer={() => (
          <div>
            <Button
              type="dashed"
              onClick={addLine}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Add Line
            </Button>
          </div>
        )}
      />

      <Card style={{ marginTop: 16, background: '#fafafa' }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Total Debit"
              value={totalDebit}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Credit"
              value={totalCredit}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Difference"
              value={getDifference()}
              precision={2}
              prefix="$"
              valueStyle={{ color: isBalanced() ? '#3f8600' : '#cf1322' }}
            />
          </Col>
        </Row>

        {validationError && (
          <Alert
            message="Validation Error"
            description={validationError}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {!validationError && isBalanced() && lines.length >= 2 && (
          <Alert
            message="Entry Balanced"
            description="Debit equals Credit - Ready to save"
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            loading={loading}
            disabled={!isBalanced() || validationError !== '' || lines.length < 2}
          >
            Save as Draft
          </Button>
          {onSubmitAndPost && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmitAndPost}
              loading={loading}
              disabled={!isBalanced() || validationError !== '' || lines.length < 2}
            >
              Save and Post
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

export default JournalEntryForm;